import zod from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import TextFieldComponent from '../../../components/TextFieldComponent';
import { Grid2 } from '@mui/material';
import ButtonComponent from '../../../components/ButtonComponent';
import {
  useCreateSevenEduIntegrationAsync,
  useGetSevenEduIntegrationByIdAsync,
  useUpdateSevenEduIntegrationAsync,
} from '../../../services/IntegrationService/SevenEduService/hooks/useMutation';
import CheckboxComponent from '../../../components/CheckboxComponent';
import { useContext, useEffect } from 'react';
import { AppContext } from '../../../context/AppContextProvider';
import { SevenEduIntegrationFormSchema } from '../../../services/IntegrationService/SevenEduService/schemas';

interface SevenEduEditionFormProps {
  sevenEduIdToUpdate?: number;
  handleSaveForm?: () => void;
}

type SevenEduEditionFormInput = zod.infer<
  typeof SevenEduIntegrationFormSchema
>;

const SevenEduEditionForm = ({
  sevenEduIdToUpdate,
  handleSaveForm,
}: SevenEduEditionFormProps) => {
  const { showSnackbar } = useContext(AppContext);
  const { control, handleSubmit, reset } = useForm<SevenEduEditionFormInput>({
    resolver: zodResolver(SevenEduIntegrationFormSchema),
    defaultValues: {
      name: '',
      description: '',
      isActive: true,
      clientId: '',
      clientSecret: '',
      baseUrl: 'https://7edu-br-dev-api.educadventista.org',
      locationId: '',
    },
  });

  const {
    mutate: mutateCreateSevenEduIntegration,
    isLoading: isLoadingCreateSevenEduIntegration,
  } = useCreateSevenEduIntegrationAsync();
  const {
    mutate: mutateUpdateSevenEduIntegration,
    isLoading: isLoadingUpdateSevenEduIntegration,
  } = useUpdateSevenEduIntegrationAsync();
  const {
    mutateAsync: mutateGetSevenEduIntegrationByIdAsync,
    isLoading: isLoadingGetSevenEduIntegrationById,
  } = useGetSevenEduIntegrationByIdAsync();

  const handleSave = (data: SevenEduEditionFormInput) => {
    const payload = { ...data, locationId: data.locationId || undefined };
    if (sevenEduIdToUpdate) {
      mutateUpdateSevenEduIntegration(
        { id: sevenEduIdToUpdate, data: payload },
        {
          onSuccess: () => {
            saveIntegration('Integração 7edu atualizada com sucesso.');
          },
        },
      );
      return;
    }
    mutateCreateSevenEduIntegration(payload, {
      onSuccess: () => {
        saveIntegration('Integração 7edu criada com sucesso.');
      },
    });
  };

  const saveIntegration = (message: string) => {
    reset();
    showSnackbar(message, 'success');
    if (handleSaveForm) handleSaveForm();
  };

  useEffect(() => {
    if (sevenEduIdToUpdate) {
      mutateGetSevenEduIntegrationByIdAsync(sevenEduIdToUpdate).then((r) =>
        reset(r, { keepDefaultValues: true }),
      );
    }
  }, [sevenEduIdToUpdate]);

  return (
    <>
      <Grid2 container columnSpacing={2}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label="Nome"
              />
            )}
            name={'name'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label="Client Id"
              />
            )}
            name={'clientId'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label="Client Secret"
              />
            )}
            name={'clientSecret'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label="Base Url"
              />
            )}
            name={'baseUrl'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label="Id da unidade escolar padrão (opcional)"
              />
            )}
            name={'locationId'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label="Descrição"
              />
            )}
            name={'description'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'right' }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <CheckboxComponent
                value={value}
                onChange={onChange}
                label="Ativo"
              />
            )}
            name={'isActive'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ mt: 2 }}>
          <ButtonComponent
            fullWidth
            onClick={handleSubmit(handleSave)}
            loading={
              isLoadingCreateSevenEduIntegration ||
              isLoadingGetSevenEduIntegrationById ||
              isLoadingUpdateSevenEduIntegration
            }
          >
            Salvar
          </ButtonComponent>
        </Grid2>
      </Grid2>
    </>
  );
};

export default SevenEduEditionForm;
